import { useEffect, useMemo, useRef, useState } from "react";
import { Loader, Center } from "@mantine/core";
import {
  IconCashBanknote,
  IconDownload,
  IconFileOff,
} from "@tabler/icons-react";
import { toast } from "sonner";
import {
  PayableInvoice,
  paymentFileDraftUrl,
  useExtractPayableFieldsMutation,
  usePayableInvoiceActionMutation,
  usePayableInvoicesQuery,
  useUpdatePayableInvoiceMutation,
} from "../../api/queries";
import { useAuth } from "../../hooks/useAuth";
import { displayCurrencyAmount } from "../../utils/display";
import { InvoiceForPaymentRow } from "./InvoiceForPaymentRow";
import {
  ForPaymentFilters,
  PaidFilters,
  StatusFilter,
} from "./InvoicesForPaymentFilters";
import { filterByPaymentDate } from "./payableFilters";
import { totalsByCurrency } from "./payableTotals";

type Tab = "for_payment" | "paid";

const TABS: { id: Tab; label: string }[] = [
  { id: "for_payment", label: "For payment" },
  { id: "paid", label: "Paid" },
];

export const InvoicesForPayment = () => {
  const { isOrgManager } = useAuth();
  const { data, isLoading, error } = usePayableInvoicesQuery();

  const [tab, setTab] = useState<Tab>("for_payment");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [vendor, setVendor] = useState("");
  const [paidFrom, setPaidFrom] = useState("");
  const [paidTo, setPaidTo] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);

  const { mutateAsync: extractFields } = useExtractPayableFieldsMutation();
  const { mutateAsync: updateInvoice } = useUpdatePayableInvoiceMutation();
  const { mutateAsync: runAction } = usePayableInvoiceActionMutation();

  const pendingExtraction = data?.pending_extraction ?? 0;
  const isExtracting = useRef(false);

  // Extraction fetches one PDF per invoice, so the server does it in bounded
  // batches. Keep requesting batches until nothing is pending. The ref guards
  // against overlapping runs when the query refetches mid-flight.
  useEffect(() => {
    if (pendingExtraction <= 0 || isExtracting.current) return;

    isExtracting.current = true;
    extractFields(10)
      .catch(() => {
        toast.error("Could not read some invoice PDFs", {
          description: "Amounts for those rows can be filled in by hand.",
        });
      })
      .finally(() => {
        isExtracting.current = false;
      });
  }, [pendingExtraction, extractFields]);

  const invoices = useMemo(() => data?.items ?? [], [data]);

  const openInvoices = useMemo(
    () => invoices.filter((invoice) => invoice.status !== "paid"),
    [invoices],
  );
  const paidInvoices = useMemo(
    () => invoices.filter((invoice) => invoice.status === "paid"),
    [invoices],
  );

  const approvedCount = useMemo(
    () => openInvoices.filter((i) => i.status === "approved").length,
    [openInvoices],
  );

  const visible = useMemo(() => {
    if (tab === "paid") {
      const byDate = filterByPaymentDate(paidInvoices, paidFrom, paidTo);
      return vendor
        ? byDate.filter((invoice) => invoice.vendor_name === vendor)
        : byDate;
    }

    return openInvoices.filter((invoice) => {
      if (status !== "all" && invoice.status !== status) return false;
      if (vendor && invoice.vendor_name !== vendor) return false;
      return true;
    });
  }, [tab, openInvoices, paidInvoices, status, vendor, paidFrom, paidTo]);

  const totals = useMemo(() => totalsByCurrency(visible), [visible]);

  const handleFieldChange = async (
    invoice: PayableInvoice,
    fields: Record<string, string | number>,
  ) => {
    try {
      await updateInvoice({ id: invoice.id, ...fields });
    } catch {
      toast.error("Could not save the change");
    }
  };

  const handleAction = async (
    invoice: PayableInvoice,
    action: "approve" | "unapprove" | "mark_paid" | "unmark_paid",
    paymentDate?: string,
  ) => {
    setBusyId(invoice.id);
    try {
      await runAction({ id: invoice.id, action, paymentDate });
    } catch {
      toast.error(
        action === "approve" || action === "unapprove"
          ? "Could not update approval"
          : "Could not update payment status",
      );
    } finally {
      setBusyId(null);
    }
  };

  const clearPaidFilters = () => {
    setPaidFrom("");
    setPaidTo("");
    setVendor("");
  };

  const switchTab = (next: Tab) => {
    setTab(next);
    setVendor("");
  };

  if (isLoading) {
    return (
      <Center className="h-full pb-24">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center className="h-full pb-24">
        <p className="text-red-600">Failed to load invoices for payment.</p>
      </Center>
    );
  }

  const emptyHint =
    tab === "paid"
      ? paidFrom || paidTo || vendor
        ? "No payments match these filters."
        : "Invoices you mark as paid move here."
      : status !== "all" || vendor
        ? "No invoices match your filters. Try adjusting them."
        : "Invoices appear here once suppliers email them to a connected mailbox.";

  return (
    <div className="flex flex-col gap-8 w-full py-8 px-4">
      <div className="flex flex-col gap-6 bg-linear-to-br from-slate-50 to-slate-100 rounded-xl shadow-lg border border-slate-200 p-6 w-full">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-linear-to-br from-slate-600 to-slate-700 text-white shadow-md">
            <IconCashBanknote size={28} stroke={2} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">
              Invoices for Payment
            </h1>
            <p className="text-sm text-gray-600">
              Approve invoices, record payments, then review what was paid
            </p>
          </div>
          {totals.length > 0 && (
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-gray-500">
                Total shown
              </p>
              {totals.map(([currency, sum]) => (
                <p key={currency} className="font-semibold text-gray-800">
                  {displayCurrencyAmount(sum, currency || "USD")}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 border-b border-gray-200">
          <div className="flex gap-1">
            {TABS.map((entry) => {
              const isActive = tab === entry.id;
              const count =
                entry.id === "paid" ? paidInvoices.length : openInvoices.length;
              return (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => switchTab(entry.id)}
                  className={
                    isActive
                      ? "px-4 py-2 text-sm font-semibold text-slate-800 border-b-2 border-slate-700 -mb-px"
                      : "px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent -mb-px"
                  }
                >
                  {entry.label}
                  <span className="ml-2 text-xs text-gray-400">{count}</span>
                </button>
              );
            })}
          </div>

          {tab === "for_payment" && (
            <a
              href={paymentFileDraftUrl}
              className={
                approvedCount > 0
                  ? "inline-flex items-center gap-2 px-3 py-1.5 mb-2 text-sm font-medium rounded-lg bg-slate-700 text-white hover:bg-slate-800"
                  : "inline-flex items-center gap-2 px-3 py-1.5 mb-2 text-sm font-medium rounded-lg bg-gray-200 text-gray-400 pointer-events-none"
              }
              title={
                approvedCount > 0
                  ? "Download a draft payment file of approved, unpaid invoices"
                  : "Approve invoices first to build a payment file"
              }
            >
              <IconDownload size={16} />
              Payment file draft
              {approvedCount > 0 && (
                <span className="text-xs opacity-80">({approvedCount})</span>
              )}
            </a>
          )}
        </div>

        {tab === "for_payment" ? (
          <ForPaymentFilters
            status={status}
            vendor={vendor}
            vendorOptions={data?.vendors ?? []}
            onStatusChange={setStatus}
            onVendorChange={setVendor}
          />
        ) : (
          <PaidFilters
            from={paidFrom}
            to={paidTo}
            vendor={vendor}
            vendorOptions={data?.vendors ?? []}
            onFromChange={setPaidFrom}
            onToChange={setPaidTo}
            onVendorChange={setVendor}
            onClear={clearPaidFilters}
          />
        )}

        {pendingExtraction > 0 && (
          <p className="text-sm text-gray-600 bg-white rounded-lg border border-gray-200 px-4 py-2">
            Reading {pendingExtraction} invoice PDF
            {pendingExtraction === 1 ? "" : "s"} to fill in amounts and dates…
          </p>
        )}

        {!isOrgManager && (
          <p className="text-sm text-gray-600 bg-white rounded-lg border border-gray-200 px-4 py-2">
            You can view and correct invoice details. Approving and recording
            payment is restricted to organization managers.
          </p>
        )}

        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-gray-200">
            <IconFileOff size={48} className="text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg">No invoices to show</p>
            <p className="text-gray-400 text-sm mt-1">{emptyHint}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-3 font-medium">Vendor</th>
                  <th className="px-3 py-3 font-medium">Description</th>
                  <th className="px-3 py-3 font-medium">Notes</th>
                  <th className="px-3 py-3 font-medium">Invoice date</th>
                  <th className="px-3 py-3 font-medium text-right">Amount</th>
                  <th className="px-3 py-3 font-medium text-center">
                    Approved
                  </th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-3 py-3 font-medium">Payment date</th>
                  <th className="px-3 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((invoice) => (
                  <InvoiceForPaymentRow
                    key={invoice.id}
                    invoice={invoice}
                    canManage={isOrgManager}
                    isBusy={busyId === invoice.id}
                    onFieldChange={(fields) =>
                      handleFieldChange(invoice, fields)
                    }
                    onToggleApproved={(approved) =>
                      handleAction(invoice, approved ? "approve" : "unapprove")
                    }
                    onTogglePaid={(paid, paymentDate) =>
                      handleAction(
                        invoice,
                        paid ? "mark_paid" : "unmark_paid",
                        paymentDate,
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesForPayment;
